import { rutaMms, obtenerRelease } from './icd-release';
import { icdGet, IcdError, ICD_ERROR } from './icd-client';
import { idDesdeUri, textoLimpio, consultaConComodin, normalizarResultado } from './icd-normalize';

// ----------------------------------------------------------------------
// Operaciones de búsqueda y detalle contra la API CIE-11.
//
// La API busca por palabras completas, así que toda consulta pasa por
// `consultaConComodin`; y no encuentra códigos en /search, para eso está
// /codeinfo. Las URIs que devuelve son canónicas de la nube: solo se usa su
// parte numérica contra el host local, nunca la URI como endpoint.
// ----------------------------------------------------------------------

const MINIMO_CARACTERES = 3;

// Tope de peticiones por lote al resolver hijos, ancestros o exclusiones: un
// capítulo tiene decenas de hijos y no se pinta ninguna lista tan larga.
const LIMITE_RESOLUCION = 30;

const comoTexto = (valor) => (typeof valor === 'string' ? valor : '');

const valorDe = (campo) => textoLimpio(campo?.['@value']);

const refExclusion = (exclusion) =>
  exclusion?.linearizationReference || exclusion?.foundationReference;

// En MMS el sufijo .Z marca el residual "sin especificación" y .Y el "otro
// especificado". El detalle no trae esos flags; el código sí los delata.
const esResidualZ = (codigo) => /\.Z$/i.test(comoTexto(codigo));
const esResidualY = (codigo) => /\.Y$/i.test(comoTexto(codigo));

// ----------------------------------------------------------------------
// Resolución de URIs a nodos
// ----------------------------------------------------------------------

const aNodo = (id, datos) => ({
  id,
  codigo: comoTexto(datos?.code).trim(),
  titulo: valorDe(datos?.title),
  esHoja: !Array.isArray(datos?.child) || datos.child.length === 0,
});

/**
 * Pide el detalle de una URI y lo reduce a nodo. Un fallo aislado devuelve
 * `null` para no tumbar el panel entero; la cancelación sí se propaga, porque
 * significa que el llamador ya descartó el resultado.
 */
async function resolverUri(uri, base, signal) {
  const id = idDesdeUri(uri);
  if (!id) return null;

  try {
    return aNodo(id, await icdGet(`${base}/${id}`, { signal }));
  } catch (error) {
    if (error?.tipo === ICD_ERROR.CANCELADO) throw error;
    return null;
  }
}

// Una URI de entidad lleva su id detrás de `/mms/` o `/entity/`. El `parent` de
// un capítulo es la RAÍZ de la linealización (`.../mms`), que no es una entidad:
// de ahí `idDesdeUri` sacaba el "11" de `/release/11/` y pedía un 404 que dejaba
// el detalle marcado como incompleto, así que ningún capítulo llegaba a cachearse.
const esUriDeEntidad = (uri) => /\/(?:mms|entity)\/\d+/i.test(String(uri ?? ''));

// Devuelven además `completo`: si alguna sub-resolución falló, el resultado es
// parcial y quien lo cachee estaría envenenando la sesión entera.
async function resolverNodos(uris, base, signal) {
  const entidades = Array.isArray(uris) ? uris.filter(esUriDeEntidad) : [];
  if (entidades.length === 0) return { nodos: [], completo: true };

  const resueltos = await Promise.all(
    entidades.slice(0, LIMITE_RESOLUCION).map((uri) => resolverUri(uri, base, signal))
  );
  return { nodos: resueltos.filter(Boolean), completo: resueltos.every(Boolean) };
}

async function resolverExclusiones(exclusiones, base, signal) {
  if (!Array.isArray(exclusiones) || exclusiones.length === 0)
    return { exclusiones: [], completo: true };

  const lista = exclusiones.slice(0, LIMITE_RESOLUCION);
  const nodos = await Promise.all(
    lista.map((exclusion) => resolverUri(refExclusion(exclusion), base, signal))
  );

  return {
    exclusiones: lista
      .map((exclusion, indice) => ({
        id: nodos[indice]?.id ?? idDesdeUri(refExclusion(exclusion)),
        etiqueta: valorDe(exclusion?.label),
        codigo: nodos[indice]?.codigo ?? '',
      }))
      .filter((exclusion) => exclusion.etiqueta),
    completo: nodos.every(Boolean),
  };
}

// `indexTerm` repite a menudo el propio título y variantes duplicadas.
const listaSinonimos = (indexTerm, titulo) => {
  if (!Array.isArray(indexTerm)) return [];

  const vistos = new Set([titulo.toLowerCase()]);
  const sinonimos = [];

  indexTerm.forEach((termino) => {
    const valor = valorDe(termino?.label);
    const clave = valor.toLowerCase();
    if (!valor || vistos.has(clave)) return;
    vistos.add(clave);
    sinonimos.push(valor);
  });

  return sinonimos;
};

/** Nodo del árbol -> Diagnostico, con la misma forma que da /search. */
const diagnosticoDesdeNodo = ({ id, codigo, titulo, esHoja }) => ({
  id,
  codigo,
  titulo,
  tituloHtml: titulo,
  capitulo: '',
  esHoja: Boolean(esHoja),
  puntaje: 0,
  sinonimoCoincidente: null,
  esResidualNoEspecificado: esResidualZ(codigo),
  esResidualOtro: esResidualY(codigo),
});

// ----------------------------------------------------------------------
// Búsqueda
// ----------------------------------------------------------------------

const filtroCapitulo = (capitulo) => {
  if (Array.isArray(capitulo)) {
    return capitulo
      .map((valor) => comoTexto(valor).trim())
      .filter(Boolean)
      .join(';');
  }
  return comoTexto(capitulo).trim();
};

// La API puede responder 200 con `error: true`. Sin esto, un fallo del
// servicio se leería como "no hay resultados", que es justo lo prohibido.
const lanzarSiRespuestaConError = (datos) => {
  if (!datos?.error) return;

  const detalle = comoTexto(datos.errorMessage).trim();
  const error = new IcdError(ICD_ERROR.SERVIDOR, null, detalle || undefined);
  if (detalle) error.message = `${error.message} (${detalle})`;
  throw error;
};

/**
 * Busca diagnósticos por texto libre. Menos de 3 caracteres no consulta.
 * @param {string} consulta texto tecleado, sin comodín
 * @param {{signal?: AbortSignal, capitulo?: string|string[], soloHojas?: boolean}} [opciones]
 * @returns {Promise<Array>} Diagnostico[]
 * @throws {IcdError}
 */
export async function buscarDiagnosticos(consulta, { signal, capitulo, soloHojas } = {}) {
  const recortada = comoTexto(consulta).trim();
  if (recortada.length < MINIMO_CARACTERES) return [];

  const base = rutaMms(await obtenerRelease());

  // useFlexisearch queda fuera a propósito: no rescata erratas y ensucia el orden.
  const params = {
    q: consultaConComodin(recortada),
    flatResults: true,
    medicalCodingMode: true,
    highlightingEnabled: true,
  };
  const capitulos = filtroCapitulo(capitulo);
  if (capitulos) params.chapterFilter = capitulos;

  const datos = await icdGet(`${base}/search`, { params, signal });
  lanzarSiRespuestaConError(datos);

  const entidades = Array.isArray(datos?.destinationEntities) ? datos.destinationEntities : [];
  const resultados = entidades.map((entidad) => normalizarResultado(entidad));

  return soloHojas ? resultados.filter((diagnostico) => diagnostico.esHoja) : resultados;
}

/**
 * Busca por código exacto ("BA00.Z"). /search no encuentra códigos: hay que
 * pasar por /codeinfo y resolver el `stemId` que devuelve.
 * @param {string} codigo
 * @param {{signal?: AbortSignal}} [opciones]
 * @returns {Promise<object|null>} Diagnostico, o null si el código no existe
 */
export async function buscarPorCodigo(codigo, { signal } = {}) {
  const limpio = comoTexto(codigo).trim().toUpperCase();
  if (!limpio) return null;

  const base = rutaMms(await obtenerRelease());

  let info;
  try {
    info = await icdGet(`${base}/codeinfo/${encodeURIComponent(limpio)}`, { signal });
  } catch (error) {
    // Un código inexistente es una respuesta válida, no un fallo que pintar.
    if (error?.tipo === ICD_ERROR.NO_ENCONTRADO) return null;
    throw error;
  }

  const id = idDesdeUri(info?.stemId);
  if (!id) return null;

  // Una sola petición, NO `obtenerDetalle`: esto corre en el camino del
  // type-ahead y resolver padres, hijos y exclusiones dispararía decenas de
  // peticiones por pulsación. La fila solo necesita código, título y si es hoja.
  const nodo = aNodo(id, await icdGet(`${base}/${id}`, { signal }));

  return {
    ...diagnosticoDesdeNodo({ ...nodo, codigo: comoTexto(info?.code).trim() || nodo.codigo }),
    puntaje: 1,
  };
}

// ----------------------------------------------------------------------
// Detalle y navegación del catálogo
// ----------------------------------------------------------------------

// Caché de detalles por id mientras dure la sesión. Solo se guarda el valor
// resuelto, nunca la promesa en vuelo: cada llamada trae su propio signal y
// compartirla haría que la cancelación de una pantalla abortara la de otra.
const cacheDetalles = new Map();

/**
 * Detalle completo de una entidad. `definition` falta en muchas entidades y
 * `child` falta en las hojas: ambos son opcionales.
 * @param {string} id id numérico de la entidad
 * @param {{signal?: AbortSignal, forzar?: boolean}} [opciones] `forzar` salta la caché
 * @returns {Promise<object|null>} DetalleDiagnostico
 */
export async function obtenerDetalle(id, { signal, forzar = false } = {}) {
  const clave = comoTexto(id).trim();
  if (!clave) return null;
  if (!forzar && cacheDetalles.has(clave)) return cacheDetalles.get(clave);

  const base = rutaMms(await obtenerRelease());
  const datos = await icdGet(`${base}/${clave}`, { signal });
  const propio = aNodo(clave, datos);

  // Un solo nivel de ancestros: la cascada recursiva multiplicaría peticiones
  // para pintar una miga de pan que solo necesita el padre inmediato. Los hijos
  // NO se resuelven aquí: resaltar un capítulo dispararía decenas de GET contra
  // un servicio compartido. Quien vaya a pintarlos llama a `obtenerHijos`.
  const [ancestros, exclusiones] = await Promise.all([
    resolverNodos(datos?.parent, base, signal),
    resolverExclusiones(datos?.exclusion, base, signal),
  ]);

  const urisHijos = Array.isArray(datos?.child) ? datos.child : [];

  const detalle = {
    id: clave,
    codigo: propio.codigo,
    titulo: propio.titulo,
    definicion: valorDe(datos?.definition),
    sinonimos: listaSinonimos(datos?.indexTerm, propio.titulo),
    exclusiones: exclusiones.exclusiones,
    ruta: [...ancestros.nodos, propio],
    // `hijos` queda SIEMPRE vacío por compatibilidad de forma; usa `totalHijos`
    // para saber si hay rama y `obtenerHijos(id)` para resolverla.
    hijos: [],
    urisHijos,
    totalHijos: urisHijos.length,
    urlNavegador: comoTexto(datos?.browserUrl),
    esHoja: propio.esHoja,
  };

  // Un 500 transitorio al resolver un padre produciría un detalle incompleto
  // que quedaría cacheado toda la sesión, Reintentar incluido.
  if (ancestros.completo && exclusiones.completo) cacheDetalles.set(clave, detalle);

  return detalle;
}

let promesaCapitulos = null;
let avisoIdiomaEmitido = false;

// El contenedor solo tiene español cargado. Si algún día no lo tuviera, la
// búsqueda respondería 500 con cuerpo vacío: no se lanza, pero queda el rastro.
// `availableLanguages` solo viene en la raíz de la release (no en el listado de
// releases), así que este es el único sitio donde se puede comprobar.
const avisarSiFaltaEspanol = (idiomas) => {
  if (avisoIdiomaEmitido) return;
  if (!Array.isArray(idiomas) || idiomas.length === 0) return;
  if (idiomas.includes('es')) return;

  avisoIdiomaEmitido = true;
  console.warn(
    `[CIE-11] El servicio no expone español (idiomas disponibles: ${idiomas.join(', ')}). Las búsquedas responderán 500 con cuerpo vacío.`
  );
};

/**
 * Los 28 capítulos de la MMS. Son fijos, así que se memoiza la promesa —y
 * nunca el rechazo, o el filtro quedaría vacío el resto de la sesión—.
 * El `codigo` devuelto es el que espera `chapterFilter` en la búsqueda.
 * @param {{signal?: AbortSignal}} [opciones]
 * @returns {Promise<Array<{id: string, codigo: string, titulo: string}>>}
 */
export async function obtenerCapitulos({ signal } = {}) {
  if (!promesaCapitulos) {
    promesaCapitulos = (async () => {
      const base = rutaMms(await obtenerRelease());
      const raiz = await icdGet(base, { signal });
      avisarSiFaltaEspanol(raiz?.availableLanguages);

      const { nodos } = await resolverNodos(raiz?.child, base, signal);
      return nodos.map(({ id, codigo, titulo }) => ({ id, codigo, titulo }));
    })().catch((error) => {
      promesaCapitulos = null;
      throw error;
    });
  }

  return promesaCapitulos;
}

// Caché de ramas ya resueltas: la segunda visita no vuelve a pedir nada. Igual
// que los detalles, una rama con resoluciones fallidas no se guarda.
const cacheHijos = new Map();

/**
 * Hijos directos de una entidad, ya resueltos. Es el único punto que gasta una
 * petición por hijo: llámalo al NAVEGAR a la rama, nunca al resaltar una fila.
 * @param {string} id
 * @param {{signal?: AbortSignal}} [opciones]
 * @returns {Promise<Array>} Diagnostico[]
 */
export async function obtenerHijos(id, { signal } = {}) {
  const clave = comoTexto(id).trim();
  if (!clave) return [];
  if (cacheHijos.has(clave)) return cacheHijos.get(clave);

  const detalle = await obtenerDetalle(clave, { signal });
  if (!detalle?.urisHijos?.length) return [];

  const base = rutaMms(await obtenerRelease());
  const { nodos, completo } = await resolverNodos(detalle.urisHijos, base, signal);
  const hijos = nodos.map((hijo) => diagnosticoDesdeNodo(hijo));

  if (completo) cacheHijos.set(clave, hijos);
  return hijos;
}
